import { toast } from "vue3-toastify";
import 'vue3-toastify/dist/index.css';
import apiClient from "@/axios";
import { saveClaimToken, getClaimToken, clearClaimToken } from "@/utils/claimTokens";
import { apiErrorMessage } from "@/utils/apiError";

const API_PATHS = {
    LINKS: 'links',
    CLAIM_LINK: 'links/{slug}/claim',
};

const state = {
    links: null,
    link: null,
    error: null,
    isLoading: false,
};

const getters = {
    stateLinks: state => state.links,
    stateLink: state => state.link,
    stateLinkError: state => state.error,
    stateIsLoading: state => state.isLoading,
};

const actions = {
    async getLinks({ commit }) {
        try {
            commit('setLoading', true);
            const response = await apiClient.get(API_PATHS.LINKS+'/');
            commit('setLinks', response.data);
        } catch (error) {
            console.error('Error fetching links:', error);
            const errorMessage = apiErrorMessage(error, "Failed to load links.");
            commit('setError', errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    // Links pointing at one file, scoped by the backend to the current user
    // (admins see everyone's). Returned to the caller instead of committed:
    // this list is per-view (file detail page) and must not clobber the
    // dashboard's `links` state.
    async getLinksForFile(_context, fileId) {
        const response = await apiClient.get(API_PATHS.LINKS + '/', {
            params: { file_id: fileId },
        });
        return response.data;
    },

    async getLink({ commit }, slug) {
        commit('setLoading', true);
        try {
            let link = await apiClient.get(`${API_PATHS.LINKS}/${slug}`);
            commit('setLink', link.data);
        } catch (error) {
            console.log(error);
            const errorMessage = apiErrorMessage(error, "Failed to load link.");
            commit('setError', errorMessage);
        } finally {
            commit('setLoading', false);
        }
    },

    async createLink({ commit }, { slug, type = 'redirect', redirectTo, fileId, pasteId }) {
        try {
            commit('setLoading', true);
            // The backend generates a unique slug when none is supplied — the
            // client must not invent one. Only send a slug if the user chose a
            // custom one.
            const payload = {
                type,
                redirect_to: redirectTo,
                file_id: fileId,
                paste_id: pasteId
            };
            const customSlug = (slug || '').trim();
            if (customSlug) payload.slug = customSlug;

            const response = await apiClient.post(API_PATHS.LINKS+'/', payload);
            // Anonymous links come back with a one-time claim token; stash it so
            // the creator can claim the link after signing in.
            if (response.data && response.data.claim_token) {
                saveClaimToken('link', response.data.slug, response.data.claim_token);
            }
            commit('addLink', response.data);
            toast.success('Link created successfully');
            return response.data;
        } catch (error) {
            console.error('Error creating link:', error);
            const errorMessage = apiErrorMessage(error, "Failed to create link.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async updateLink({ commit }, { slug, redirectTo, newSlug, type = 'redirect', fileId, extras }) {
        try {
            commit('setLoading', true);
            // Only send the destination field that matches the link type — a
            // file link has no redirect URL and vice versa (sending an empty
            // redirect_to used to 422).
            const payload = { slug: newSlug || slug, type };
            if (type === 'file') {
                payload.file_id = fileId;
            } else {
                payload.redirect_to = redirectTo;
            }
            // Micro-site customization fields (splash + preview overrides);
            // the backend applies only the keys that are present.
            if (extras) Object.assign(payload, extras);
            const response = await apiClient.put(`${API_PATHS.LINKS}/${slug}`, payload);
            commit('setLink', response.data);
            // Pass the pre-rename slug so the list row can be found after a rename.
            commit('updateLink', { ...response.data, _previousSlug: slug });
            toast.success('Link updated successfully');
            return response.data;
        } catch (error) {
            console.error('Error updating link:', error);
            const errorMessage = apiErrorMessage(error, "Failed to update link.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async deleteLink({ commit }, slug) {
        try {
            commit('setLoading', true);
            await apiClient.delete(`${API_PATHS.LINKS}/${slug}`);
            commit('removeLink', slug);
            toast.success('Link deleted successfully');
        } catch (error) {
            console.error('Error deleting link:', error);
            const errorMessage = apiErrorMessage(error, "Failed to delete link.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async claimLink({ commit }, slug) {
        try {
            commit('setLoading', true);
            const response = await apiClient.post(
                API_PATHS.CLAIM_LINK.replace('{slug}', slug),
                { claim_token: getClaimToken('link', slug) }
            );
            clearClaimToken('link', slug);
            commit('updateLink', response.data);
            toast.success('Link claimed successfully');
            return response.data;
        } catch (error) {
            console.error('Error claiming link:', error);
            const errorMessage = apiErrorMessage(error, "Failed to claim link.");
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

};

const mutations = {
    setLinks(state, links) {
        state.links = links;
    },
    setLink(state, link) {
        state.link = link;
    },
    addLink(state, link) {
        if (!state.links) state.links = [];
        state.links.unshift(link);
    },
    updateLink(state, payload) {
        const { _previousSlug, ...updatedLink } = payload;
        const oldSlug = _previousSlug || updatedLink.slug;
        if (state.links) {
            const index = state.links.findIndex(
                link => link.slug === updatedLink.slug || link.slug === oldSlug
            );
            if (index !== -1) {
                state.links.splice(index, 1, updatedLink);
            }
        }
        if (state.link && (state.link.slug === updatedLink.slug || state.link.slug === oldSlug)) {
            state.link = updatedLink;
        }
    },
    removeLink(state, slug) {
        if (state.links) {
            state.links = state.links.filter(link => link.slug !== slug);
        }
        if (state.link && state.link.slug === slug) {
            state.link = null;
        }
    },
    setError(state, error) {
        state.error = error;
    },
    setLoading(state, isLoading) {
        state.isLoading = isLoading;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
